import type { IModelConnection } from "@itwin/core-frontend";

import { Lighting } from "../../domain/elements/Lighting";
import {
  queryAllDOTPayItemsForElement,
} from "../queries/dotPayItem/dotPayItemQuery";
import { queryBaseElementProps } from "../queries/elementPropsQueries";
import { queryDrainageUtilitiesForElement } from "../queries/drainageItem/drainageUtilitiesQueries";
import { queryUtilityFootprintForElement } from "../queries/UtilityGeneral/queryUtilityFootprintForElement";
import { queryOriginForElement } from "../queries/originQuery";
import { queryFeatureForElement } from "../queries/featureQueries";
import { queryUtilityQualityForElement } from "../queries/UtilityGeneral/queryUtilityQualityForElement";
import { queryUtilityDataForElement } from "../queries/UtilityGeneral/queryUtilitydata";
import { extractTriMeshesForElement } from "../../iTwin/geometry/meshExtractor";
import { extractIfcCurvesForElement } from "../../iTwin/geometry/curveExtractor";

type ElementBuildTimingSink = {
  basePropsMs?: number;
  metadataMs?: number;
  geometryMs?: number;
};

function timingNowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function addTiming(
  sink: ElementBuildTimingSink | undefined,
  key: keyof ElementBuildTimingSink,
  startMs: number
): void {
  if (!sink) return;
  const elapsed = Math.max(0, timingNowMs() - startMs);
  sink[key] = (sink[key] ?? 0) + elapsed;
}

async function queryLightingBuildMetadata(
  iModel: IModelConnection,
  elementId: string
) {
  const [
    originResult,
    dotPayItemsResult,
    featureResult,
    drainAndUtiliyItemResult,
    utilityFootprintResult,
    utilityQualityResult,
    utilityDataResult,
  ] = await Promise.allSettled([
    queryOriginForElement(iModel, elementId),
    queryAllDOTPayItemsForElement(iModel, elementId),
    queryFeatureForElement(iModel, elementId),
    queryDrainageUtilitiesForElement(iModel, elementId),
    queryUtilityFootprintForElement(iModel, elementId),
    queryUtilityQualityForElement(iModel, elementId),
    queryUtilityDataForElement(iModel, elementId),
  ]);

  if (originResult.status === "rejected") {
    console.warn(`[IFC Export] Lighting metadata query failed (Origin) for ${elementId}:`, originResult.reason);
  }
  if (dotPayItemsResult.status === "rejected") {
    console.warn(`[IFC Export] Lighting metadata query failed (DOTPayItems) for ${elementId}:`, dotPayItemsResult.reason);
  }
  if (featureResult.status === "rejected") {
    console.warn(`[IFC Export] Lighting metadata query failed (Feature) for ${elementId}:`, featureResult.reason);
  }
  if (drainAndUtiliyItemResult.status === "rejected") {
    console.warn(
      `[IFC Export] Lighting metadata query failed (DrainageUtilities) for ${elementId}:`,
      drainAndUtiliyItemResult.reason
    );
  }
  if (utilityFootprintResult.status === "rejected") {
    console.warn(
      `[IFC Export] Lighting metadata query failed (UtilityFootprint) for ${elementId}:`,
      utilityFootprintResult.reason
    );
  }
  if (utilityQualityResult.status === "rejected") {
    console.warn(
      `[IFC Export] Lighting metadata query failed (UtilityQuality) for ${elementId}:`,
      utilityQualityResult.reason
    );
  }
  if (utilityDataResult.status === "rejected") {
    console.warn(`[IFC Export] Lighting metadata query failed (UtilityData) for ${elementId}:`, utilityDataResult.reason);
  }

  const origin = originResult.status === "fulfilled" ? originResult.value : undefined;
  const dotPayItems = dotPayItemsResult.status === "fulfilled" ? dotPayItemsResult.value : [];
  const feature = featureResult.status === "fulfilled" ? featureResult.value : undefined;
  const drainAndUtiliyItem =
    drainAndUtiliyItemResult.status === "fulfilled" ? drainAndUtiliyItemResult.value : undefined;
  const utilityFootprint =
    utilityFootprintResult.status === "fulfilled" ? utilityFootprintResult.value : undefined;
  const utilityQuality =
    utilityQualityResult.status === "fulfilled" ? utilityQualityResult.value : undefined;
  const utilityData = utilityDataResult.status === "fulfilled" ? utilityDataResult.value : undefined;

  const dotPayItem1 = dotPayItems.find((item) => Number((item as any)?.Index) === 1);
  const dotPayItem2 = dotPayItems.find((item) => Number((item as any)?.Index) === 2);

  return {
    origin,
    dotPayItem1,
    dotPayItem2,
    dotPayItems,
    feature,
    drainAndUtiliyItem,
    utilityFootprint,
    utilityQuality,
    utilityData,
  };
}

export async function buildLightingForElement(
  iModel: IModelConnection,
  elementId: string,
  timing?: ElementBuildTimingSink
): Promise<Lighting | undefined> {
  const baseStartMs = timingNowMs();
  const base = await queryBaseElementProps(iModel, elementId);
  addTiming(timing, "basePropsMs", baseStartMs);
  if (!base) return undefined;

  // These lookups are independent once base element props exist.
  const metadataStartMs = timingNowMs();
  const metadataPromise = queryLightingBuildMetadata(iModel, elementId).then((result) => {
    addTiming(timing, "metadataMs", metadataStartMs);
    return result;
  });
  const geometryStartMs = timingNowMs();
  const meshPromise = extractTriMeshesForElement(iModel, elementId, {
    makeLocal: true,
    unitScale: 1.0,
    maxPolyfaces: Number.POSITIVE_INFINITY,
  }).then((result) => {
    addTiming(timing, "geometryMs", geometryStartMs);
    return result;
  });
  const [
    metadata,
    meshes,
  ] = await Promise.all([
    metadataPromise,
    meshPromise,
  ]);

  return new Lighting({
    Model: base.Model,
    LastMod: base.LastMod,
    CodeSpec: base.CodeSpec,
    CodeScope: base.CodeScope,
    CodeValue: base.CodeValue,
    UserLabel: base.UserLabel,
    Parent: base.Parent,
    FederationGUID: base.FederationGUID,
    JSONProperties: base.JSONProperties,
    Origin: metadata.origin,
    Meshes: meshes,
    DOTPayItem1: metadata.dotPayItem1,
    DOTPayItem2: metadata.dotPayItem2,
    DOTPayItems: metadata.dotPayItems,
    DrainageUtilities: metadata.drainAndUtiliyItem,
    UtilityFootPrint: metadata.utilityFootprint,
    UtilityQuality: metadata.utilityQuality,
    UtilityData: metadata.utilityData,
    Feature: metadata.feature,
  });
}

export async function buildLightingCurvesForElement(
  iModel: IModelConnection,
  elementId: string,
  timing?: ElementBuildTimingSink
): Promise<Lighting | undefined> {
  const baseStartMs = timingNowMs();
  const base = await queryBaseElementProps(iModel, elementId);
  addTiming(timing, "basePropsMs", baseStartMs);
  if (!base) return undefined;

  // Curve-export path skips mesh extraction entirely.
  const metadataStartMs = timingNowMs();
  const metadataPromise = queryLightingBuildMetadata(iModel, elementId).then((result) => {
    addTiming(timing, "metadataMs", metadataStartMs);
    return result;
  });
  const geometryStartMs = timingNowMs();
  const curvePromise = extractIfcCurvesForElement(iModel, elementId, {
    makeLocal: true,
    unitScale: 1.0,
  }).then((result) => {
    addTiming(timing, "geometryMs", geometryStartMs);
    return result;
  });
  const [
    metadata,
    curves,
  ] = await Promise.all([
    metadataPromise,
    curvePromise,
  ]);

  return new Lighting({
    Model: base.Model,
    LastMod: base.LastMod,
    CodeSpec: base.CodeSpec,
    CodeScope: base.CodeScope,
    CodeValue: base.CodeValue,
    UserLabel: base.UserLabel,
    Parent: base.Parent,
    FederationGUID: base.FederationGUID,
    JSONProperties: base.JSONProperties,
    Origin: metadata.origin,
    Curves: curves,
    DOTPayItem1: metadata.dotPayItem1,
    DOTPayItem2: metadata.dotPayItem2,
    DOTPayItems: metadata.dotPayItems,
    DrainageUtilities: metadata.drainAndUtiliyItem,
    UtilityFootPrint: metadata.utilityFootprint,
    UtilityQuality: metadata.utilityQuality,
    UtilityData: metadata.utilityData,
    Feature: metadata.feature,
  });
}

export async function buildLightingCurvesGeometryOnlyForElement(
  iModel: IModelConnection,
  elementId: string,
  timing?: ElementBuildTimingSink
): Promise<Lighting | undefined> {
  const baseStartMs = timingNowMs();
  const base = await queryBaseElementProps(iModel, elementId);
  addTiming(timing, "basePropsMs", baseStartMs);
  if (!base) return undefined;

  const metadataStartMs = timingNowMs();
  const originPromise = queryOriginForElement(iModel, elementId).then((result) => {
    addTiming(timing, "metadataMs", metadataStartMs);
    return result;
  });
  const geometryStartMs = timingNowMs();
  const curvePromise = extractIfcCurvesForElement(iModel, elementId, {
    makeLocal: true,
    unitScale: 1.0,
  }).then((result) => {
    addTiming(timing, "geometryMs", geometryStartMs);
    return result;
  });
  const [origin, curves] = await Promise.all([
    originPromise,
    curvePromise,
  ]);

  return new Lighting({
    Model: base.Model,
    LastMod: base.LastMod,
    CodeSpec: base.CodeSpec,
    CodeScope: base.CodeScope,
    CodeValue: base.CodeValue,
    UserLabel: base.UserLabel,
    Parent: base.Parent,
    FederationGUID: base.FederationGUID,
    JSONProperties: base.JSONProperties,
    Origin: origin,
    Curves: curves,
  });
}
